import { useRef } from 'react';
import { useReduxState } from 'hooks';
import { FormApi } from 'informed';
import api from 'api';
import { ConfirmButton } from 'components/common';
import { Form, TextArea } from 'components/common/form';

interface Props {
  achievementId: number;
  actionText: string;
  allowEmptyText?: boolean;
  buttonText: string;
  confirmText: string;
  confirmTitle: string;
  successMessage: string;
  templateItemId: number;
  targetUserId: number;
  textInfoMessage?: string;
  textPlaceholder: string;
  updateUserAchievement?: () => Promise<void>;
}

const AchievementCompletionsFillTemplateAdd = ({
  achievementId,
  actionText,
  allowEmptyText = true,
  buttonText,
  confirmText,
  confirmTitle,
  successMessage,
  targetUserId,
  templateItemId,
  textInfoMessage,
  textPlaceholder,
  updateUserAchievement
}: Props) => {
  const formApi = useRef<FormApi>(undefined);

  const { messages, theme } = useReduxState();

  const handleConfirm = async (values) => {
    await handleSubmit(values);
    return true;
  };

  const handleSubmit = async (values) => {
    await api.post(
      `/admin/achievements/${achievementId}/completions/${targetUserId}/items`,
      { items: { [templateItemId]: values.text || null } }
    );
    if (updateUserAchievement) await updateUserAchievement();
  };

  const validateText = (value) => {
    if (allowEmptyText) return undefined;

    return value?.trim() ? undefined : messages.commonFieldRequired;
  };

  const validateClick = () => {
    const formState = formApi.current?.getState();
    const isValid = !validateText(formState.values?.text);
    if (!isValid) formApi.current?.submitForm();
    return isValid;
  };

  return (
    <div className="add">
      <Form getApi={(form) => (formApi.current = form)} onSubmit={handleSubmit}>
        {({ formState }) => (
          <>
            <TextArea
              autoGrow={true}
              error={formState?.errors?.text}
              field="text"
              id={`achievement-item-text-${templateItemId}`}
              infoMessage={textInfoMessage}
              placeholder={textPlaceholder}
              rows={3}
              {...(allowEmptyText
                ? {}
                : {
                    validate: validateText,
                    validateOnChange: true
                  })}
            />

            <ConfirmButton
              actionText={actionText}
              buttonSize="small"
              buttonStyle={{ marginTop: 5 }}
              buttonText={buttonText}
              category="success"
              confirmText={confirmText}
              confirmTitle={confirmTitle}
              validateBeforeClick={validateClick}
              onConfirm={() => handleConfirm(formState.values)}
              successMessage={successMessage}
            />
          </>
        )}
      </Form>

      <style jsx>{`
        .add {
          background-color: ${theme.lightGreyLighter};
          border-radius: 7px;
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default AchievementCompletionsFillTemplateAdd;
